import React, { ChangeEventHandler, Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { extractUploadedFilesFromZip, FileFromBrowserUpload, UploadedFile } from '@paragon/component-processor';

import { convertFiles } from '../../actions/convertFiles';
import fileTypes from '../../data/fileTypes';

const fileTypesAcceptedForUpload = [
  ...fileTypes.alpine.fileExtensions,
  ...fileTypes.mitek.fileExtensions,
  ...fileTypes.simpson.fileExtensions,
  'zip',
] as const;
const fileTypesToAcceptForUploadHtmlAccept = fileTypesAcceptedForUpload.map((fileType) => `.${fileType}`).join(',');
const convertToFileTypes = [
  ...fileTypes.mitek.fileExtensions,
  ...fileTypes.simpson.fileExtensions,
  'trs',
  'tps',
] as const;

type ConvertToFileType = (typeof convertToFileTypes)[number];

function getFilesOfTypesAcceptedForUpload(files: readonly UploadedFile[]) {
  return files.filter((file) =>
    fileTypesAcceptedForUpload.map((fileType) => fileType.toLowerCase()).includes(file.filetype!.toLowerCase()),
  );
}

function resetEventTargetValue(event: React.MouseEvent<HTMLInputElement>) {
  (event.target as any).value = null;
}

type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = DispatchProps;

interface State {
  readonly convertToFileType: ConvertToFileType;
  readonly fileData: readonly UploadedFile[] | null;
  readonly numberOfFilesBeingConverted: number;
  readonly inputErrorText: string | null;
}

class ConvertControls extends Component<Props, State> {
  state: State = {
    convertToFileType: fileTypes.simpson.fileExtensions[0],
    fileData: null,
    numberOfFilesBeingConverted: 0,
    inputErrorText: null,
  };

  onSelectConvertToType = (value: ConvertToFileType) => {
    this.setState({ convertToFileType: value });
  };

  onSetFile: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const parsedFiles = [...event.target.files!].map((file) => new FileFromBrowserUpload(file));
    const filesOfFileTypesAcceptedForUpload = getFilesOfTypesAcceptedForUpload(parsedFiles);

    const zipFiles = filesOfFileTypesAcceptedForUpload.filter((file) => file.filetype!.toLowerCase() === 'zip');
    let numberOfFilesBeingConverted = filesOfFileTypesAcceptedForUpload.length - zipFiles.length;
    await Promise.all(
      zipFiles.map(async (zipFile) => {
        const filesInZipFile = await extractUploadedFilesFromZip(zipFile.contents!);
        const filesInZipFileAcceptedForUpload = getFilesOfTypesAcceptedForUpload(filesInZipFile);
        numberOfFilesBeingConverted += filesInZipFileAcceptedForUpload.length;
      }),
    );

    this.setState({
      fileData: filesOfFileTypesAcceptedForUpload,
      numberOfFilesBeingConverted,
    });
  };

  onConvert = () => {
    const inputErrorText = [];
    if (this.state.convertToFileType == null) {
      inputErrorText.push('Select a format to convert to.');
    }
    if (this.state.fileData == null) {
      inputErrorText.push('Select file(s) to convert.');
    }
    if (inputErrorText.length > 0) {
      this.setState({ inputErrorText: inputErrorText.join(' ') });
      return;
    }

    this.props.convertFiles(this.state.convertToFileType, this.state.fileData!, this.state.numberOfFilesBeingConverted);

    this.setState({
      inputErrorText: null,
      fileData: null,
    });
  };

  render() {
    let selectFilesButtonText = 'Select Files';
    if (this.state.fileData != null) {
      if (this.state.numberOfFilesBeingConverted === 1) selectFilesButtonText = '1 file';
      else selectFilesButtonText = `${this.state.numberOfFilesBeingConverted} files`;
    }

    return (
      <>
        <Box>{this.state.inputErrorText}</Box>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, mx: 5 }}>
            <PropertySelectField
              property="Convert to type"
              options={convertToFileTypes}
              value={this.state.convertToFileType}
              submit={this.onSelectConvertToType}
            />
          </Box>
          <Box sx={{ flex: 1, mt: 2, mb: 1, mx: 5 }}>
            <input
              id="upload-input"
              type="file"
              accept={fileTypesToAcceptForUploadHtmlAccept}
              multiple
              onChange={this.onSetFile}
              onClick={resetEventTargetValue}
              hidden
            />
            <label htmlFor="upload-input">
              <Button variant="outlined" fullWidth component="span">
                {selectFilesButtonText}
              </Button>
            </label>
          </Box>
          <Box sx={{ flex: 1, mt: 2, mb: 1, mx: 5 }}>
            <Button variant="outlined" fullWidth onClick={this.onConvert}>
              Convert
            </Button>
          </Box>
        </Box>
      </>
    );
  }
}

const actionCreators = {
  convertFiles,
};

export default connect(null, actionCreators)(ConvertControls);
